import { Check, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  type CompareRow,
  compareRows,
  corroborations,
  memberDisplayName,
  percent,
  sharedNgrams,
} from '@/features/entity-resolution/groupFormat'
import type { GroupMember, IdentityAssessment } from '@/services/api/entity-resolution-groups'
import { cn } from '@/utils/cn'
import { IdentityAssessmentNotice } from './IdentityAssessmentNotice'
import { ComparatorIcon } from './parts'

interface HeadToHeadPanelProps {
  comparison: {
    survivor: GroupMember
    member: GroupMember
    similarity: number
    assessment?: IdentityAssessment
  }
  busy: boolean
  on: { merge: () => void; reject: () => void; close: () => void }
}

/** Right-hand head-to-head: canonical ⟷ candidate, property comparison, and a
 *  breakdown of how the match score was computed. */
export function HeadToHeadPanel({ comparison, busy, on }: HeadToHeadPanelProps) {
  const { survivor, member, similarity, assessment } = comparison
  return (
    // Below lg the review panel scrolls as one, so the card lets its header (close)
    // and its footer (the decision) stick to the edges of that scroll; the footer's
    // negative offset is the panel's bottom padding.
    <Card className="flex min-w-0 w-full flex-col rounded-xl p-0 lg:w-1/2 lg:overflow-hidden">
      <div className="sticky top-0 z-10 flex shrink-0 items-center justify-between rounded-t-xl border-b border-border bg-card px-4 py-3 lg:static lg:rounded-none lg:bg-transparent">
        <span className="text-3xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          Head-to-head
        </span>
        <button
          type="button"
          onClick={on.close}
          className="-m-2 p-2 text-muted-foreground hover:text-foreground lg:m-0 lg:p-0"
        >
          <X className="size-4" />
        </button>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <IdentityAssessmentNotice assessment={assessment} showAll />
        <div className="grid grid-cols-2 gap-3">
          <div className="min-w-0 rounded-lg border border-primary/30 bg-primary/5 p-3">
            <div className="text-3xs uppercase tracking-wider text-primary">Canonical</div>
            <div className="mt-0.5 whitespace-pre-wrap text-sm font-semibold text-foreground [overflow-wrap:anywhere]">
              {memberDisplayName(survivor)}
            </div>
          </div>
          <div className="min-w-0 rounded-lg border border-border bg-muted/40 p-3">
            <div className="text-3xs uppercase tracking-wider text-muted-foreground">Candidate</div>
            <div className="mt-0.5 whitespace-pre-wrap text-sm font-semibold text-foreground [overflow-wrap:anywhere]">
              {memberDisplayName(member)}
            </div>
          </div>
        </div>

        <ComparisonTable rows={compareRows(survivor, member)} />
        <ScoreBreakdown survivor={survivor} member={member} similarity={similarity} />
      </div>

      <div className="sticky -bottom-4 flex shrink-0 flex-col gap-2 rounded-b-xl border-t border-border bg-card p-3 md:-bottom-6 lg:static lg:flex-row lg:rounded-none lg:bg-transparent">
        <Button
          disabled={busy}
          onClick={on.merge}
          className="h-auto min-h-10 min-w-0 whitespace-normal bg-primary py-2 text-primary-foreground hover:bg-primary/90 lg:flex-1"
        >
          <Check className="size-4" />
          <span className="min-w-0 [overflow-wrap:anywhere]">
            Merge into {memberDisplayName(survivor)}
          </span>
        </Button>
        <Button variant="outline" disabled={busy} onClick={on.reject}>
          <X className="size-4" />
          Not a match
        </Button>
      </div>
    </Card>
  )
}

function ComparisonTable({ rows }: { rows: CompareRow[] }) {
  return (
    <div className="space-y-px">
      {rows.map(row => (
        <div
          key={row.key}
          className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-x-3 gap-y-1 px-1 py-1.5"
        >
          <span className="col-span-3 text-3xs uppercase tracking-wider text-muted-foreground [overflow-wrap:anywhere]">
            {row.label}
          </span>
          <Value text={row.left} emphasize={row.key === 'name'} />
          <ComparatorIcon status={row.status} />
          <Value text={row.right} emphasize={row.key === 'name'} align="right" />
        </div>
      ))}
    </div>
  )
}

function Value({
  text,
  emphasize = false,
  align = 'left',
}: {
  text: string | null
  emphasize?: boolean
  align?: 'left' | 'right'
}) {
  return (
    <span
      className={cn(
        'min-w-0 whitespace-pre-wrap text-sm [overflow-wrap:anywhere]',
        align === 'right' && 'text-right',
        text === null ? 'text-muted-foreground italic' : 'text-foreground',
        emphasize && 'font-medium'
      )}
    >
      {text ?? '—'}
    </span>
  )
}

function ScoreBreakdown({
  survivor,
  member,
  similarity,
}: {
  survivor: GroupMember
  member: GroupMember
  similarity: number
}) {
  const grams = sharedNgrams(memberDisplayName(survivor), memberDisplayName(member))
  const corr = corroborations(survivor, member)
  const pct = percent(similarity)

  return (
    <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-3">
      <div className="flex items-center justify-between">
        <span className="text-3xs uppercase tracking-wider text-muted-foreground">
          How this score was computed
        </span>
        <span className="rounded-full bg-warning-surface px-2 py-0.5 text-3xs font-semibold text-warning-foreground">
          {pct}
        </span>
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between text-xs">
          <span className="text-foreground">Name similarity · character n-grams</span>
          <span className="font-semibold text-warning-foreground">{pct}</span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-muted">
          <div className="h-full bg-warning" style={{ width: pct }} />
        </div>
      </div>

      {grams.length > 0 && (
        <div>
          <div className="mb-1.5 text-3xs text-muted-foreground">
            Compared because their names share n-grams:
          </div>
          <div className="flex flex-wrap gap-1">
            {grams.map(g => (
              <span
                key={g}
                className="rounded bg-muted px-1.5 py-0.5 font-mono text-3xs text-muted-foreground"
              >
                {g}
              </span>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="mb-1.5 text-3xs text-muted-foreground">Corroborating fields:</div>
        {corr.length === 0 ? (
          <p className="text-3xs italic text-muted-foreground">No shared fields beyond the name.</p>
        ) : (
          <div className="space-y-1">
            {corr.map(c => (
              <div key={c.label} className="flex items-center gap-2 text-xs">
                {c.ok ? (
                  <Check className="size-3.5 text-success" />
                ) : (
                  <X className="size-3.5 text-muted-foreground/60" />
                )}
                <span className={c.ok ? 'text-foreground' : 'text-muted-foreground'}>
                  {c.label}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-3xs text-muted-foreground">
        Surfaces for review when name similarity ≥ 80%. The final decision stays human.
      </p>
    </div>
  )
}
